// Wyoming AOI detail. Renders metadata + TRS + roster sourced from
// wy_ownership. Roster splits into "Working interest" and "Broad
// roster" (interested_party) tabs. Add-to-lead-list reuses the same
// SelectionBar pattern as the Weld AOI detail.

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { wyApi, type WyAoiDetailResponse, type WyRosterRow } from '../services/wyApi';
import { useRosterSelection } from '../components/useRosterSelection';
import { SelectionBar } from '../components/SelectionBar';
import { PushedIndicator } from '../components/PushedIndicator';
import { SkipMarker } from '../components/SkipMarker';
import { HasContactIndicator } from '../components/HasContactIndicator';

type Tab = 'all' | 'working_interest' | 'interested_party';

export function WyAoiDetail() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [data, setData] = useState<WyAoiDetailResponse | null>(null);
  const [err, setErr]   = useState<string | null>(null);
  const [tab, setTab]   = useState<Tab>('all');
  const [unpinning, setUnpinning] = useState(false);
  const selection = useRosterSelection(id ? `wy_aoi:${id}` : undefined);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try { setData(await wyApi.getAoi(id)); }
      catch (e: any) { setErr(e?.message ?? 'failed to load'); }
    })();
  }, [id]);

  async function doUnpin() {
    if (!id || !confirm('Unpin this Wyoming AOI? Ownership data is unaffected.')) return;
    setUnpinning(true);
    try { await wyApi.unpinAoi(id); nav('/mi/wy/aois'); }
    catch (e: any) { setErr(e?.message ?? 'unpin failed'); setUnpinning(false); }
  }

  const filteredRoster = useMemo(() => {
    if (!data) return [];
    if (tab === 'all') return data.roster;
    if (tab === 'working_interest')
      return data.roster.filter(r => r.wi_rows_in_aoi > 0);
    return data.roster.filter(r => r.wi_rows_in_aoi === 0);
  }, [data, tab]);

  if (err) return (
    <div className="px-8 py-7 max-w-[1280px] mx-auto">
      <Link to="/mi/wy/aois" className="text-[12px] text-text-faint font-mono hover:text-text-dim">← all Wyoming AOIs</Link>
      <div className="mt-4 text-[13px] text-red bg-red/10 border border-red/40 rounded px-4 py-3">{err}</div>
    </div>
  );
  if (!data) return <div className="px-8 py-7 text-[12px] text-text-faint font-mono">loading…</div>;

  const trsLabel = data.aoi.trs
    .map(t => `T${t.township_num}${t.township_dir} R${t.range_num}${t.range_dir} Sec ${t.section}`)
    .join(' · ');

  return (
    <div className="px-8 py-7 max-w-[1280px] mx-auto">
      <Link to="/mi/wy/aois" className="text-[12px] text-text-faint font-mono hover:text-text-dim">← all Wyoming AOIs</Link>
      <header className="mt-2 flex items-end justify-between pb-6 border-b border-border">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-text-faint">wyoming · aoi</div>
          <div className="font-display text-[28px] leading-tight">
            {data.aoi.name === trsLabel
              ? trsLabel
              : <><span>{data.aoi.name}</span><span className="text-text-dim text-[18px] ml-3">{trsLabel}</span></>}
          </div>
          <div className="text-[12px] text-text-dim mt-2 font-mono space-x-2">
            <span>{data.aoi.basin ?? 'no basin'}</span>
            <span className="text-text-ghost">·</span>
            <span>{data.counts.all} parties</span>
            <span className="text-text-ghost">·</span>
            <span>{data.counts.working_interest} working interest</span>
            <span className="text-text-ghost">·</span>
            <span>pinned {data.aoi.pinned_at?.slice(0, 10)}</span>
          </div>
        </div>
        <button onClick={doUnpin} disabled={unpinning}
          className="px-3 py-2 text-[12px] text-text-dim border border-border rounded hover:text-red hover:border-red/50">
          {unpinning ? 'Unpinning…' : 'Unpin'}
        </button>
      </header>

      <div className="mt-6">
        <div className="flex items-center gap-1 border-b border-border">
          {(['all','working_interest','interested_party'] as Tab[]).map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-3 py-2.5 text-[12px] font-mono uppercase tracking-wide border-b-2 ${
                tab === t ? 'text-accent-bright border-accent' : 'text-text-dim border-transparent hover:text-text'
              }`}>
              {labelForTab(t)} <span className="text-text-faint ml-1">{tabCount(t, data)}</span>
            </button>
          ))}
        </div>

        <div className="mt-3">
          <div className="grid grid-cols-[26px_2fr_1fr_120px_120px_120px] items-center gap-3 px-4 py-2 text-[10px] font-mono uppercase tracking-wider text-text-faint border-b border-border">
            <div></div>
            <div>Party</div>
            <div>Type</div>
            <div className="text-right">Owns rows</div>
            <div className="text-right">WI rows</div>
            <div className="text-right">Max decimal</div>
          </div>
          {filteredRoster.length === 0 && (
            <div className="px-4 py-8 text-center text-[12px] text-text-faint font-mono">no parties in this bucket</div>
          )}
          {filteredRoster.map(r => (
            <div key={r.party_id}
              className="grid grid-cols-[26px_2fr_1fr_120px_120px_120px] items-center gap-3 px-4 py-3 border-b border-border last:border-b-0 hover:bg-bg-hover/30">
              <input type="checkbox" checked={selection.isSelected(r.party_id)}
                onChange={() => selection.toggle({
                  party_id: r.party_id,
                  display_name: r.display_name,
                  has_phone: r.has_phone,
                  has_address: r.has_address,
                  source_label: r.display_name,
                })}
                className="accent-accent" />
              <div>
                <Link to={`/mi/party/${r.party_id}`}
                  className={'text-[13px] hover:text-accent ' + (r.skipped_at ? 'text-text-faint line-through' : 'text-text')}>
                  {r.display_name_natural ?? r.display_name}
                </Link>
                <PushedIndicator pushedAt={r.last_pushed_to_close_at} className="ml-1.5" />
                <SkipMarker     skippedAt={r.skipped_at} className="ml-1.5" />
                <HasContactIndicator hasPhone={r.has_phone} hasAddress={r.has_address} className="ml-1.5 align-middle" />
                <div className="text-[10px] text-text-faint font-mono mt-0.5">
                  {r.normalized_name}
                  {(r.skip_trace_city || r.skip_trace_state) && (
                    <span className="ml-2">{[r.skip_trace_city, r.skip_trace_state].filter(Boolean).join(', ')}</span>
                  )}
                </div>
              </div>
              <div>
                <span className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded bg-text-faint/15 text-text-faint">
                  {r.entity_type}
                </span>
              </div>
              <div className="text-right text-[12px] text-text font-mono">{r.ownership_rows_in_aoi}</div>
              <div className="text-right text-[12px] text-text font-mono">{r.wi_rows_in_aoi || '—'}</div>
              <div className="text-right text-[12px] text-text-dim font-mono">
                {r.max_decimal_interest !== null ? r.max_decimal_interest.toFixed(8) : '—'}
              </div>
            </div>
          ))}
        </div>
      </div>

      <SelectionBar
        selection={selection}
        sourceLabel={`Wyoming AOI · ${data.aoi.name}`}
        source_kind="wy_aoi"
        source_id={id!}
        onPush={() => {/* push wired via existing PushConfirmModal in future Wyoming-push session */}}
      />
    </div>
  );
}

function labelForTab(t: Tab): string {
  if (t === 'working_interest') return 'Working interest';
  if (t === 'interested_party') return 'Broad roster';
  return 'All';
}
function tabCount(t: Tab, data: WyAoiDetailResponse): number {
  if (t === 'all') return data.counts.all;
  if (t === 'working_interest') return data.counts.working_interest;
  return data.counts.interested_party;
}
